import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "@/config/apiBase";
import { motion } from "framer-motion";
import { 
  Users, Crown, UserPlus, Mail, Trash2, RefreshCw, 
  AlertTriangle, ArrowRightLeft, Shield, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";

const FamilySettings = () => {
  const { user, setUser } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [familyData, setFamilyData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [transferStatus, setTransferStatus] = useState(null);
  const [selectedTransferMember, setSelectedTransferMember] = useState("");
  const [transferAction, setTransferAction] = useState("remain");
  const [isTransferring, setIsTransferring] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isAcceptingInvite, setIsAcceptingInvite] = useState(false);

  // Handle invite param in URL - auto-accept if present
  useEffect(() => {
    const inviteId = searchParams.get("invite");
    if (inviteId && user && !isAcceptingInvite) {
      acceptInviteFromUrl(inviteId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  const acceptInviteFromUrl = async (inviteId) => {
    setIsAcceptingInvite(true);
    console.log("[FamilySettings] Auto-accepting invite from URL:", inviteId);
    
    try {
      const response = await axios.post(`${API}/family/invite/${inviteId}/accept`);
      console.log("[FamilySettings] Invite accepted:", response.data);
      
      toast.success("Welcome to the family! 🎉");
      
      // Clear the invite param from URL
      searchParams.delete("invite");
      setSearchParams(searchParams, { replace: true });
      
      // Clear any localStorage pending invite
      localStorage.removeItem("pendingFamilyInvite");
      
      // Refresh user data
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      
      // Refresh family data
      fetchFamilyData();
    } catch (error) {
      console.error("[FamilySettings] Failed to accept invite:", error);
      const message = error.response?.data?.detail || "Failed to accept invite";
      toast.error(message);
      
      // Clear the invite param from URL even on error
      searchParams.delete("invite");
      setSearchParams(searchParams, { replace: true });
      localStorage.removeItem("pendingFamilyInvite");
    } finally {
      setIsAcceptingInvite(false);
    }
  };

  // Debug logging
  useEffect(() => {
    console.log("[FamilySettings] User state:", {
      subscription_plan: user?.subscription_plan,
      family_group_id: user?.family_group_id,
      family_role: user?.family_role
    });
  }, [user]);

  // Always fetch family data when component mounts (Settings.jsx controls visibility)
  useEffect(() => {
    fetchFamilyData();
    fetchTransferStatus();
  }, []);

  const fetchFamilyData = async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      console.log("[FamilySettings] Fetching family data from:", `${API}/family/group`);
      const response = await axios.get(`${API}/family/group`);
      console.log("[FamilySettings] Family data response:", response.data);
      setFamilyData(response.data);
    } catch (error) {
      console.error("[FamilySettings] Failed to fetch family data:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to load family information";
      setFetchError(errorMsg);
      // Don't toast error - show it inline instead
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransferStatus = async () => {
    try {
      const response = await axios.get(`${API}/family/transfer-status`);
      setTransferStatus(response.data);
    } catch (error) {
      console.error("[Family] Failed to fetch transfer status:", error);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      if (response.data) {
        setUser(response.data);
      }
    } catch (error) {
      console.error("[Family] Failed to refresh user:", error);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsInviting(true);
    try {
      const response = await axios.post(`${API}/family/invite`, { email: inviteEmail });
      
      if (response.data.email_sent) {
        toast.success(`Invite sent to ${inviteEmail}`);
      } else {
        // Invite created but email failed - still success but warn user
        toast.success(`Invite created for ${inviteEmail}`);
        if (response.data.email_error) {
          toast.warning("Email notification could not be sent. Share the invite link manually.");
        }
      }
      
      setInviteEmail("");
      fetchFamilyData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send invite";
      toast.error(message);
    } finally {
      setIsInviting(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    setActionLoading(prev => ({ ...prev, [inviteId]: true }));
    try {
      const response = await axios.post(`${API}/family/invite/${inviteId}/resend`);
      
      if (response.data.email_sent) {
        toast.success("Invite email resent successfully");
      } else {
        toast.success("Invite extended");
        if (response.data.email_error) {
          toast.warning("Email could not be sent. Share the invite link manually.");
        }
      }
      
      fetchFamilyData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to resend invite";
      toast.error(message);
    } finally {
      setActionLoading(prev => ({ ...prev, [inviteId]: false }));
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    setActionLoading(prev => ({ ...prev, [inviteId]: true }));
    try {
      await axios.delete(`${API}/family/invite/${inviteId}`);
      toast.success("Invite revoked");
      fetchFamilyData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to revoke invite";
      toast.error(message);
    } finally {
      setActionLoading(prev => ({ ...prev, [inviteId]: false }));
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    setActionLoading(prev => ({ ...prev, [memberId]: true }));
    try {
      await axios.delete(`${API}/family/members/${memberId}`);
      toast.success(`${memberName} has been removed from your family plan`);
      fetchFamilyData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to remove member";
      toast.error(message);
    } finally {
      setActionLoading(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const handleLeaveFamily = async () => {
    setActionLoading(prev => ({ ...prev, leave: true }));
    try {
      await axios.post(`${API}/family/leave`);
      toast.success("You have left the family plan");
      await refreshUser();
      fetchFamilyData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to leave family";
      toast.error(message);
    } finally {
      setActionLoading(prev => ({ ...prev, leave: false }));
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedTransferMember) {
      toast.error("Please select a member to transfer ownership to");
      return;
    }

    setIsTransferring(true);
    try {
      await axios.post(`${API}/family/transfer-ownership`, {
        new_owner_user_id: selectedTransferMember,
        old_owner_action: transferAction
      });
      toast.success("Ownership transferred successfully. The new owner must claim billing to continue the subscription.");
      setSelectedTransferMember("");
      await refreshUser();
      fetchFamilyData();
      fetchTransferStatus();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to transfer ownership";
      toast.error(message);
    } finally {
      setIsTransferring(false);
    }
  };

  const handleClaimOwnership = async () => {
    setIsClaiming(true);
    try {
      const response = await axios.post(`${API}/family/claim-ownership`);
      if (response.data.redirect_url) {
        window.location.href = response.data.redirect_url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to claim ownership";
      toast.error(message);
    } finally {
      setIsClaiming(false);
    }
  };

  // Show accepting invite state
  if (isAcceptingInvite) {
    return (
      <motion.div 
        className="card-contact"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-2 mb-6">
          <Users className="w-5 h-5 text-[#E07A5F]" />
          <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
            Family Plan
          </h2>
        </div>
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-[#81B29A] mb-4" />
          <p className="text-[#3D405B] font-medium">Accepting your invite...</p>
          <p className="text-sm text-[#6D6F7C] mt-1">Please wait while we add you to the family</p>
        </div>
      </motion.div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <motion.div 
        className="card-contact"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-2 mb-6">
          <Users className="w-5 h-5 text-[#E07A5F]" />
          <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
            Family Plan
          </h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#E07A5F]" />
          <span className="ml-2 text-[#6D6F7C]">Loading family info...</span>
        </div>
      </motion.div>
    );
  }

  // Error state - show friendly message with retry
  if (fetchError && !familyData) {
    return (
      <motion.div 
        className="card-contact"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="family-error-section"
      >
        <div className="flex items-center gap-2 mb-6">
          <Users className="w-5 h-5 text-[#E07A5F]" />
          <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
            Family Plan
          </h2>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-3" />
          <p className="text-amber-800 font-medium mb-2">Unable to load family information</p>
          <p className="text-sm text-amber-700 mb-4">{fetchError}</p>
          <Button
            onClick={fetchFamilyData}
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-100 rounded-xl"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </motion.div>
    );
  }

  // User is in a family group
  const isOwner = familyData?.is_owner;
  const members = familyData?.members || [];
  const invites = familyData?.invites || [];
  const seats = familyData?.seats || { used: 0, pending: 0, limit: 5, available: 5 };
  const group = familyData?.group;

  // Get non-owner members for transfer dropdown
  const transferableMembers = members.filter(m => m.role !== "owner");

  return (
    <motion.div 
      className="card-contact"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="family-settings-section"
    >
      <div className="flex items-center gap-2 mb-6">
        <Users className="w-5 h-5 text-[#E07A5F]" />
        <h2 className="text-lg font-semibold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
          Family Plan
        </h2>
        {isOwner && (
          <span className="text-xs bg-[#F2CC8F]/30 text-[#E07A5F] px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
            <Crown className="w-3 h-3" /> Owner
          </span>
        )}
      </div>

      {/* Pending Transfer Banner (for new owner) */}
      {transferStatus?.has_pending_transfer && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="claim-ownership-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">Ownership Transfer Pending</p>
              <p className="text-sm text-amber-700 mt-1">
                You&apos;ve been assigned as the new Family owner. Set up billing to keep the Family plan active.
              </p>
              <Button
                onClick={handleClaimOwnership}
                disabled={isClaiming}
                className="mt-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl"
                data-testid="claim-ownership-btn"
              >
                {isClaiming ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <>Claim Ownership & Set Up Billing</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Section A: Plan & Seats */}
      <div className="bg-[#F4F1EA]/50 rounded-xl p-4 mb-6" data-testid="family-seats-info">
        <div className="flex items-center justify-between mb-3">
          <span className="font-medium text-[#3D405B]">Family Plan</span>
          <span className={`text-xs px-2 py-1 rounded-full ${
            group?.status === "active" 
              ? "bg-[#81B29A]/20 text-[#81B29A]" 
              : "bg-amber-100 text-amber-700"
          }`}>
            {group?.status === "active" ? "Active" : group?.status || "Active"}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-[#3D405B]" data-testid="seats-used">{seats.used}</p>
            <p className="text-xs text-[#6D6F7C]">Members</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#F2CC8F]" data-testid="seats-pending">{seats.pending}</p>
            <p className="text-xs text-[#6D6F7C]">Pending</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#81B29A]" data-testid="seats-available">{seats.available}</p>
            <p className="text-xs text-[#6D6F7C]">Available</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-[#6D6F7C]" data-testid="seats-limit">{seats.limit}</p>
            <p className="text-xs text-[#6D6F7C]">Max Seats</p>
          </div>
        </div>
      </div>

      {/* Section B: Members */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-[#3D405B] mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#6D6F7C]" />
          Members ({members.length})
        </h3>
        <div className="space-y-2" data-testid="family-members-list">
          {members.map((member) => (
            <div 
              key={member.user_id}
              className="flex items-center justify-between p-3 bg-white border border-[#F4F1EA] rounded-xl"
              data-testid={`family-member-${member.user_id}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#E07A5F] to-[#F2CC8F] rounded-full flex items-center justify-center text-white font-bold">
                  {member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div>
                  <p className="font-medium text-[#3D405B] flex items-center gap-2">
                    {member.name || member.email}
                    {member.role === "owner" && (
                      <Crown className="w-4 h-4 text-[#F2CC8F]" />
                    )}
                  </p>
                  <p className="text-xs text-[#6D6F7C]">{member.email}</p>
                </div>
              </div>
              
              {/* Remove button (owner only, not for self) */}
              {isOwner && member.user_id !== user?.user_id && member.role !== "owner" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:bg-red-50"
                      disabled={actionLoading[member.user_id]}
                      data-testid={`remove-member-${member.user_id}`}
                    >
                      {actionLoading[member.user_id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove Family Member</AlertDialogTitle>
                      <AlertDialogDescription>
                        Remove <strong>{member.name || member.email}</strong> from your Family plan? 
                        They will keep their account and data, but will be moved to the Free plan.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleRemoveMember(member.user_id, member.name || member.email)}
                        className="bg-red-500 hover:bg-red-600 rounded-xl"
                      >
                        Remove Member
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Section C: Pending Invites (Owner only) */}
      {isOwner && invites.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-[#3D405B] mb-3 flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#6D6F7C]" />
            Pending Invites ({invites.length})
          </h3>
          <div className="space-y-2" data-testid="family-invites-list">
            {invites.map((invite) => (
              <div 
                key={invite.invite_id}
                className="flex items-center justify-between p-3 bg-[#F2CC8F]/10 border border-[#F2CC8F]/30 rounded-xl"
                data-testid={`family-invite-${invite.invite_id}`}
              >
                <div>
                  <p className="font-medium text-[#3D405B]">{invite.invited_email}</p>
                  <p className="text-xs text-[#6D6F7C]">
                    Expires: {new Date(invite.expires_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResendInvite(invite.invite_id)}
                    disabled={actionLoading[invite.invite_id]}
                    className="text-[#81B29A] hover:bg-[#81B29A]/10"
                    data-testid={`resend-invite-${invite.invite_id}`}
                  >
                    {actionLoading[invite.invite_id] ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevokeInvite(invite.invite_id)}
                    disabled={actionLoading[invite.invite_id]}
                    className="text-red-500 hover:bg-red-50"
                    data-testid={`revoke-invite-${invite.invite_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section D: Invite Member (Owner only) */}
      {isOwner && seats.available > 0 && (
        <>
          <Separator className="bg-[#F4F1EA] my-6" />
          <div data-testid="invite-member-form">
            <h3 className="text-sm font-semibold text-[#3D405B] mb-3 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-[#6D6F7C]" />
              Invite Family Member
            </h3>
            <form onSubmit={handleInvite} className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="flex-1 border-[#F4F1EA] rounded-xl"
                data-testid="invite-email-input"
              />
              <Button
                type="submit"
                disabled={isInviting || !inviteEmail.trim()}
                className="bg-[#81B29A] hover:bg-[#6FA289] text-white rounded-xl"
                data-testid="send-invite-btn"
              >
                {isInviting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite
                  </>
                )}
              </Button>
            </form>
            <p className="text-xs text-[#6D6F7C] mt-2">
              Invites expire after 7 days. Each member gets their own private account.
            </p>
          </div>
        </>
      )}

      {/* Section E: Transfer Ownership (Owner only) */}
      {isOwner && transferableMembers.length > 0 && (
        <>
          <Separator className="bg-[#F4F1EA] my-6" />
          <div data-testid="transfer-ownership-section">
            <h3 className="text-sm font-semibold text-[#3D405B] mb-3 flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-[#6D6F7C]" />
              Transfer Ownership
            </h3>
            <p className="text-xs text-[#6D6F7C] mb-4">
              Transfer ownership to another family member. The new owner will need to set up billing.
            </p>
            
            <div className="space-y-4">
              <div>
                <Label className="text-[#3D405B] text-sm">Transfer to</Label>
                <Select value={selectedTransferMember} onValueChange={setSelectedTransferMember}>
                  <SelectTrigger className="w-full bg-white border-[#F4F1EA] rounded-xl mt-1" data-testid="transfer-member-select">
                    <SelectValue placeholder="Select a family member" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                    {transferableMembers.map((member) => (
                      <SelectItem key={member.user_id} value={member.user_id}>
                        {member.name || member.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[#3D405B] text-sm">After transfer, I want to:</Label>
                <Select value={transferAction} onValueChange={setTransferAction}>
                  <SelectTrigger className="w-full bg-white border-[#F4F1EA] rounded-xl mt-1" data-testid="transfer-action-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                    <SelectItem value="remain">Remain as a family member</SelectItem>
                    <SelectItem value="leave">Leave the family plan</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={!selectedTransferMember || isTransferring}
                    className="w-full border-[#F4F1EA] rounded-xl"
                    data-testid="transfer-ownership-btn"
                  >
                    {isTransferring ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transferring...</>
                    ) : (
                      <><ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer Ownership</>
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Transfer Family Ownership</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to transfer ownership? The new owner will need to set up billing to continue the Family plan. 
                      {transferAction === "leave" && " You will be removed from the family and moved to the Free plan."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleTransferOwnership}
                      className="bg-[#E07A5F] hover:bg-[#D0694E] rounded-xl"
                    >
                      Confirm Transfer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </>
      )}

      {/* Leave Family (Member only, not owner) */}
      {!isOwner && familyData?.has_family && (
        <>
          <Separator className="bg-[#F4F1EA] my-6" />
          <div data-testid="leave-family-section">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full text-red-500 hover:bg-red-50 rounded-xl"
                  disabled={actionLoading.leave}
                  data-testid="leave-family-btn"
                >
                  {actionLoading.leave ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <><Shield className="w-4 h-4 mr-2" /> Leave Family Plan</>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Leave Family Plan?</AlertDialogTitle>
                  <AlertDialogDescription>
                    If you leave, you&apos;ll be moved to the Free plan. Your account and data will be kept.
                    You can be invited back later if there&apos;s an available seat.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleLeaveFamily}
                    className="bg-red-500 hover:bg-red-600 rounded-xl"
                  >
                    Leave Family
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default FamilySettings;
